import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { StylishNewToggleButtonGroup } from 'components/DesignSystems/New/StylishNewToggleButtonGroup';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useActivationActions } from 'hooks/incident/useActivationActions';
import { MemberActivationStatusProps } from './types';
import { selectUserGuid } from 'slices/commonSelectors';

const MemberActivationStatus: React.FC<MemberActivationStatusProps> = ({
  row,
  incidentId,
  simplified = false,
  currentUserHasApprovalAuthority = false,
}) => {
  const userGuid = useSelector(selectUserGuid);
  const { mutateAsync: updateActivation, isPending } = useActivationActions();
  const [showDeclineNote, setShowDeclineNote] = useState(false);
  const [declineNote, setDeclineNote] = useState('');

  const handleActivationUpdate = async (isActivated: boolean) => {
    await updateActivation({
      incidentId,
      memberUserGuid: row.userId,
      isActivated,
      declineNote: isActivated ? undefined : declineNote,
    });
    setShowDeclineNote(false);
    setDeclineNote('');
  };

  return (
    <>
      {/* Activation Controls Section */}
      {!row.activatedTimestamp &&
        !row.declinedTimestamp &&
        (row.userId === userGuid || currentUserHasApprovalAuthority) && (
          <div className="activation-controls mt-3 p-3 border rounded">
            <div className="fw-bold mb-2">Activation Response</div>
            {row.userId !== userGuid && !currentUserHasApprovalAuthority && (
              <div className="alert alert-warning">
                You do not have permission to activate other users.
              </div>
            )}
            {(row.userId === userGuid || currentUserHasApprovalAuthority) && (
              <>
                {!showDeclineNote ? (
                  <StylishNewToggleButtonGroup
                    data={[
                      { label: 'Activate', value: 'activate' },
                      { label: 'Decline', value: 'decline' },
                    ]}
                    selected={''}
                    onChange={(value) => {
                      if (value === 'activate') {
                        handleActivationUpdate(true);
                      } else {
                        setShowDeclineNote(true);
                      }
                    }}
                    disabled={isPending}
                  />
                ) : (
                  <div>
                    <textarea
                      className="form-control mb-2"
                      placeholder="Enter decline reason (optional, max 160 characters)"
                      value={declineNote}
                      onChange={(e) =>
                        setDeclineNote(e.target.value.slice(0, 160))
                      }
                      maxLength={160}
                      rows={3}
                    />
                    <div className="d-flex gap-2">
                      <StylishNewButton
                        className="button--primary"
                        onClick={() => handleActivationUpdate(false)}
                        disabled={isPending}
                      >
                        Submit Decline
                      </StylishNewButton>
                      <StylishNewButton
                        className="button--secondary"
                        onClick={() => {
                          setShowDeclineNote(false);
                          setDeclineNote('');
                        }}
                        disabled={isPending}
                      >
                        Cancel
                      </StylishNewButton>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

      {/* Display existing activation/decline info */}
      {(row.activatedTimestamp || row.declinedTimestamp) && !simplified && (
        <div className="activation-status mt-3 p-3 border rounded">
          <div className="fw-bold mb-2">Activation Status</div>
          {row.activatedTimestamp && (
            <div className="text-success">
              Activated at:{' '}
              {dayjs(row.activatedTimestamp).format('MMM D, YYYY h:mm A')}
            </div>
          )}
          {row.declinedTimestamp && (
            <>
              <div className="text-danger">
                Declined at:{' '}
                {dayjs(row.declinedTimestamp).format('MMM D, YYYY h:mm A')}
              </div>
              {row.activationNote && (
                <div className="mt-2">
                  <span className="fw-bold">Reason:</span> {row.activationNote}
                </div>
              )}
            </>
          )}

          {/* Allow user to change their activation status */}
          {row.userId === userGuid && (
            <div className="mt-3">
              {!showDeclineNote ? (
                <div className="d-flex gap-2">
                  {row.declinedTimestamp && (
                    <StylishNewButton
                      className="button--primary"
                      onClick={() => handleActivationUpdate(true)}
                      disabled={isPending}
                    >
                      Change to Activated
                    </StylishNewButton>
                  )}
                  {row.activatedTimestamp && (
                    <StylishNewButton
                      className="button--secondary"
                      onClick={() => setShowDeclineNote(true)}
                      disabled={isPending}
                    >
                      Change to Declined
                    </StylishNewButton>
                  )}
                </div>
              ) : (
                <div>
                  <textarea
                    className="form-control mb-2"
                    placeholder="Enter decline reason (optional, max 160 characters)"
                    value={declineNote}
                    onChange={(e) =>
                      setDeclineNote(e.target.value.slice(0, 160))
                    }
                    maxLength={160}
                    rows={3}
                  />
                  <div className="d-flex gap-2">
                    <StylishNewButton
                      className="button--primary"
                      onClick={() => handleActivationUpdate(false)}
                      disabled={isPending}
                    >
                      Submit Decline
                    </StylishNewButton>
                    <StylishNewButton
                      className="button--secondary"
                      onClick={() => {
                        setShowDeclineNote(false);
                        setDeclineNote('');
                      }}
                      disabled={isPending}
                    >
                      Cancel
                    </StylishNewButton>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default MemberActivationStatus;
